import AppButton from '@nxsys/components/AppButton';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useCheckSubDepartmentAssign } from '@nxsys/hooks/api/company';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { countryList } from '@nxsys/utils';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { showMessage } from '@nxsys/utils/message';
import { Col, Form, Row, Tooltip } from 'antd';
import { FormProps } from 'antd/lib';
import { isEqual, uniqueId } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

export type DepartmentItem = {
  department: string;
  prefix?: string;
  entityId?: number | null;
  isSameAsCompany?: boolean;
  companyName?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  city?: string;
  county?: string;
  postCode?: string;
  country?: { label: string; value: string };
  email?: string;
};

export type DepartmentValues = {
  departments: Array<DepartmentItem>;
};

type Props = {
  data?: ICompany;
  updateCompany: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const Department: React.FC<Props> = ({ data, updateCompany }) => {
  const { messages, formatMessage } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm<DepartmentValues>();
  const watchDepartmentList = Form.useWatch('departments', form);
  const addDepartmentRef = useRef<
    ((defaultValue?: Partial<DepartmentItem>) => void) | null
  >(null);

  const departmentData = useMemo(() => {
    if (isIreland) {
      return (data?.departments ?? []).map((item: any) => ({
        department: item.departmentName ?? item.department ?? '',
        prefix: item.prefix ?? '',
        entityId: item.id ?? null,
        isSameAsCompany: item.isSameAsCompany ?? true,
        companyName: item.companyName ?? '',
        address1: item.address?.[0] ?? '',
        address2: item.address?.[1] ?? '',
        address3: item.address?.[2] ?? '',
        address4: item.address?.[3] ?? '',
        city: item.city ?? '',
        county: item.county ?? '',
        postCode: item.postCode ?? '',
        country: item.country
          ? (countryList.find((c) => c.value === item.country) ?? undefined)
          : undefined,
        email: item.email ?? '',
        id: `COMPANY_${data?.id}_DEPARTMENT_${uniqueId()}`,
      }));
    }

    // UK: the `departments` relation is the single source of truth —
    // `departmentList` is a derived, name-only cache the backend keeps for
    // old clients and no longer needs reading here.
    return (data?.departments ?? []).map((item: any) => ({
      department: item.departmentName ?? '',
      prefix: item.prefix ?? '',
      entityId: item.id ?? null,
      id: `COMPANY_${data?.id}_DEPARTMENT_${uniqueId()}`,
    }));
  }, [data, isIreland]);

  useEffect(() => {
    if (departmentData) {
      form.setFieldsValue({ departments: departmentData });
    }
  }, [departmentData]);

  // The rows as the save sends them. `departmentList` is derived server-side
  // from the saved `departments` relation — no longer sent from here.
  const toPayload = (items: DepartmentItem[] = []) =>
    isIreland
      ? items.map((item) => ({
          id: item.entityId || undefined,
          departmentName: item.department,
          prefix: item.prefix || null,
          isSameAsCompany: item.isSameAsCompany ?? true,
          companyName: item.companyName ?? null,
          address: [
            item.address1,
            item.address2,
            item.address3,
            item.address4,
          ].filter(Boolean),
          email: item.email ?? null,
          postCode: item.postCode ?? null,
          city: item.city ?? null,
          county: item.county ?? null,
          country: item.country?.value ?? null,
        }))
      : items.map((item) => ({
          id: item.entityId || undefined,
          departmentName: item.department,
          prefix: item.prefix || null,
        }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    const departments = toPayload(values.departments);
    // Nothing changed (e.g. a delete that was refused): no save, so no
    // "Updated successfully" that reads as if the delete went through.
    if (isEqual(departments, toPayload(departmentData))) {
      showMessage({
        type: 'warning',
        content: messages['common.noChangesToSave'],
      });
      return;
    }
    await updateCompany({ departments });
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const { run: checkSubDepartmentAssign } = useCheckSubDepartmentAssign();
  const handleDeleteDepartment = async (index: number, cb: () => void) => {
    const item = watchDepartmentList[index];
    // A row with no entityId was never saved — nothing to check server-side.
    if (!item.entityId) {
      cb();
      return;
    }
    const { employees, userGroups } = await checkSubDepartmentAssign({
      departmentId: item.entityId,
      companyId: data?.id ?? '',
    });
    // The row stays when either still uses the department.
    if (employees > 0) {
      showMessage({
        type: 'error',
        content: messages['common.cannotDeleteDepartment'],
      });
    } else if (userGroups?.length) {
      showMessage({
        type: 'error',
        content: formatMessage(
          { id: 'common.cannotDeleteDepartmentUsedByGroups' },
          { groups: userGroups.join(', ') },
        ),
      });
    } else {
      cb();
    }
  };

  const renderIrelandLayout = () => (
    <Form.List name="departments">
      {(fields, { add, remove }) => {
        addDepartmentRef.current = (defaultValue) =>
          add({
            department: '',
            isSameAsCompany: true,
            ...defaultValue,
          });
        return (
          <>
            {fields.map((field, index) => {
              const isSameAsCompany =
                watchDepartmentList?.[index]?.isSameAsCompany ?? true;

              return (
                <div
                  key={field.key}
                  className="border border-neutral-200 rounded-[8px] p-[16px] mb-[12px]"
                >
                  <div className="flex items-start gap-x-[12px]">
                    <div className="flex-1">
                      <Row gutter={[16, 0]}>
                        <Col span={16}>
                          <Form.Item
                            label={messages['common.department']}
                            name={[field.name, 'department']}
                            rules={[
                              {
                                required: true,
                                message:
                                  messages['common.pleaseEnterDepartmentName'],
                              },
                              {
                                validator: (_, value) => {
                                  if (!value) return Promise.resolve();
                                  const isDuplicate = (
                                    watchDepartmentList ?? []
                                  )
                                    .slice(0, index)
                                    .some(
                                      (d: DepartmentItem) =>
                                        d?.department === value,
                                    );
                                  return isDuplicate
                                    ? Promise.reject(
                                        new Error('Department already exists!'),
                                      )
                                    : Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <AppInput
                              placeholder={
                                messages['common.enterDepartmentName']
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label={messages['common.prefix']}
                            name={[field.name, 'prefix']}
                          >
                            <AppInput
                              placeholder=""
                              maxLength={20}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        name={[field.name, 'isSameAsCompany']}
                        valuePropName="checked"
                        className="mb-[8px]"
                      >
                        <AppCheckbox>
                          Invoicing Details Same as Employer Details?
                        </AppCheckbox>
                      </Form.Item>

                      {!isSameAsCompany && (
                        <div className="mt-[12px]">
                          <Row gutter={[16, 0]}>
                            <Col span={12}>
                              <Form.Item
                                label="Employer Name"
                                name={[field.name, 'companyName']}
                              >
                                <AppInput placeholder="Employer Name" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Address 1"
                                name={[field.name, 'address1']}
                              >
                                <AppInput placeholder="Address 1" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Address 2"
                                name={[field.name, 'address2']}
                              >
                                <AppInput placeholder="Address 2" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Address 3"
                                name={[field.name, 'address3']}
                              >
                                <AppInput placeholder="Address 3" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Address 4"
                                name={[field.name, 'address4']}
                              >
                                <AppInput placeholder="Address 4" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="City/Town"
                                name={[field.name, 'city']}
                              >
                                <AppInput placeholder="City/Town" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="County"
                                name={[field.name, 'county']}
                              >
                                <AppInput placeholder="Enter county" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label={messages['common.eircode']}
                                name={[field.name, 'postCode']}
                                rules={[
                                  postCodeValidationRule(
                                    messages,
                                    payrollCountry,
                                  ),
                                ]}
                              >
                                <AppInput
                                  placeholder={messages['common.eircode']}
                                  upperCaseAll
                                  onChange={(e) => {
                                    const formatted = formatPostCode(
                                      e.target.value,
                                      payrollCountry,
                                    );
                                    form.setFields([
                                      {
                                        name: [
                                          'departments',
                                          field.name,
                                          'postCode',
                                        ],
                                        value: formatted,
                                      },
                                    ]);
                                  }}
                                  maxLength={8}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Country"
                                name={[field.name, 'country']}
                              >
                                <AppSelect
                                  options={countryList}
                                  placeholder="Country"
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Email"
                                name={[field.name, 'email']}
                                rules={[
                                  {
                                    type: 'email',
                                    message: 'Please enter a valid email',
                                  },
                                ]}
                              >
                                <AppInput
                                  placeholder="Email"
                                  allowCapitalize={false}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </div>

                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        handleDeleteDepartment(field.name, () => {
                          remove(field.name);
                        });
                      }}
                    >
                      <Icons.IconTrash />
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        );
      }}
    </Form.List>
  );

  const renderTableLayout = () => (
    <Form.List name="departments">
      {(fields, { add, remove }) => {
        addDepartmentRef.current = add;
        return (
          <AppTableContainer
            data={fields}
            rowKey="key"
            pagination={false}
            loading={false}
            page={0}
            total={fields.length}
            pageSize={fields.length}
            scroll={{ x: 300 }}
            columns={[
              {
                key: 'index',
                title: messages['common.index'],
                dataIndex: 'index',
                width: 80,
                render: (_: any, __: any, index: number) => index + 1,
              },
              {
                key: 'department',
                title: messages['common.department'],
                dataIndex: 'department',
                render: (_: any, field: any) => (
                  <Form.Item
                    name={[field.name, 'department']}
                    rules={[
                      {
                        required: true,
                        message: messages['common.pleaseEnterDepartmentName'],
                      },
                    ]}
                    className="mb-0"
                  >
                    <AppInput
                      placeholder={messages['common.enterDepartmentName']}
                    />
                  </Form.Item>
                ),
              },
              {
                key: 'prefix',
                title: messages['common.prefix'],
                dataIndex: 'prefix',
                width: 150,
                render: (_: any, field: any) => (
                  <Form.Item
                    name={[field.name, 'prefix']}
                    className="mb-0"
                  >
                    <AppInput
                      placeholder=""
                      maxLength={20}
                    />
                  </Form.Item>
                ),
              },
              {
                key: 'actions',
                title: messages['common.action'],
                dataIndex: 'actions',
                width: 100,
                fixed: 'right',
                align: 'left',
                render: (_: any, field: any) => (
                  <Tooltip title="Delete">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() => {
                        handleDeleteDepartment(field.name, () => {
                          remove(field.name);
                        });
                      }}
                    >
                      <Icons.IconDelete color="red" />
                    </div>
                  </Tooltip>
                ),
              },
            ]}
            titleEmpty={messages['common.thereIsNoDepartmentCreatedYet']}
          />
        );
      }}
    </Form.List>
  );

  return (
    <div className="Department">
      <div className="p-[16px]">
        <div className="flex items-center justify-end gap-x-2 mb-[16px]">
          <AppButton
            leftIcon={<Icons.IconPlus />}
            variant="outlined"
            name={messages['common.addDepartment']}
            onClick={() => {
              addDepartmentRef.current?.(
                isIreland
                  ? { department: '', isSameAsCompany: true }
                  : { department: '' },
              );
            }}
          />
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={() => form.submit()}
          />
        </div>
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          {isIreland ? renderIrelandLayout() : renderTableLayout()}
        </AppFormContainer>
      </div>
    </div>
  );
};

export default Department;
